// src/components/CommercialGuide.jsx
//
// Internal documentation for the commercial dashboard and the ambassador
// network: what moves a lead through each status today, the pages where
// ambassadors and admins act, kiosk installation and the handover to the
// owner. Traced from the backend code (utils/ambassador/visits.ts,
// utils/ambassador/prospectFacility.ts, routes/facilities_v2.ts,
// utils/commercialLeadSync.ts, routes/webhooks/paddle.ts,
// routes/publicBookDemo.ts, scripts/importSirene.ts) so it stays honest
// about what's live.
//
// Same auth+fetch boilerplate as AgentDetail.jsx / CommercialDashboard.jsx
// - this codebase has no shared auth hook, each dashboard page does its
// own, so this follows that convention. This page fetches nothing itself
// (it's static content), the auth check is just to keep it behind the
// same gate as the rest of the commercial dashboard.
import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container, Typography, Box, Button, Paper, Stack, Chip, Divider,
  Table, TableBody, TableCell, TableHead, TableRow, Link,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { initKeycloak } from '../keycloak';
import { INK, statusColor, statusLabel } from '../utils/commercialColors';

// Pages d'entrée par fonctionnalité (qrproofs.com/?page=..., HomePage.jsx
// LANDINGS) and their Google Ads campaigns. Built: the 5 pages, the origin of
// demos and signups (utm/gclid), the anonymous visit count and
// /admin/landings. Not built: cookie banner + Google tag (conversions in
// Google Ads). Keep the status chips honest when they land.
const LANDING_PAGES = [
  ['/?page=borne', 'Borne de notation', 'restaurants, bars, boulangeries', 'borne avis client restaurant · borne de notation · avis clients sur place'],
  ['/?page=avis-verifies', 'Avis vérifiés', 'tout établissement exposé aux faux avis', 'faux avis restaurant · avis vérifiés · avis clients fiables'],
  ['/?page=conciergerie', 'Conciergerie', 'gîtes, chambres d’hôtes, hôtels, campings', 'gestion avis gîte · signalement client hôtel · conciergerie location saisonnière'],
  ['/?page=statistiques', 'Statistiques & quartier', 'gérants qui pilotent leur établissement', 'statistiques restaurant · comparer restaurant quartier · analyse avis clients'],
  ['/?page=evenements', 'Événements dans l’app', 'restaurants avec plat du jour, bars à happy hour', 'promouvoir plat du jour · événement restaurant application · attirer clients quartier'],
];

const LANDING_STATUS = [
  ['Maquettes des 5 pages', true],
  ['Pages publiées sur le site (qrproofs.com/?page=…)', true],
  ['Comptage des visites et des conversions par page et par mot-clé', true],
  ['Tableau de bord /admin/landings', true],
  ['Bandeau cookies et balise Google Ads (Consent Mode)', false],
];

const GOOGLE_ADS_STEPS = [
  'Créer le compte sur ads.google.com avec le compte Google de l’entreprise, en mode Expert (pas en campagne « intelligente »), et ajouter la facturation.',
  'Créer deux actions de conversion (Objectifs › Conversions › Nouvelle action › Site Web) : « Demande de démo » et « Inscription ». Transmettre l’identifiant AW-… et les deux libellés à l’équipe technique : ils vont dans la configuration du site.',
  'Paramètres du compte › Suivi : activer le marquage automatique (gclid) et mettre en suffixe de l’URL finale : utm_source=google&utm_medium=cpc&utm_campaign={campaignid}&utm_content={adgroupid}&utm_term={keyword}. Google y met lui-même le mot-clé qui a déclenché l’annonce, et le site le garde sur le prospect.',
  'Une campagne Réseau de Recherche par page (URL finale = la page, ex. https://qrproofs.com/?page=borne), toutes avec le même budget quotidien et la même zone (ville ou région ciblée), Réseau Display décoché : c’est ce qui rend les pages comparables.',
  'Dans chaque campagne, des groupes d’annonces par thème, avec les mots-clés de départ ci-dessus en « expression » ou « exact », et des mots-clés négatifs (emploi, recrutement, formation, gratuit, avis Google…). Les annonces reprennent l’accroche de la page.',
  'Enchères : « Maximiser les clics » au démarrage, puis « Maximiser les conversions » quand une campagne a une trentaine de conversions.',
  'Laisser tourner 3 à 4 semaines sans tout changer. Ne comparer une page ou un mot-clé qu’à partir d’environ 100 clics : en dessous, les écarts sont du hasard.',
  'Comparer le taux de conversion (démos + inscriptions ÷ clics) et le coût par conversion, par page puis par mot-clé. Couper les mots-clés qui dépensent sans convertir, renforcer la page gagnante ; elle pourra devenir l’accueil du site.',
];

const cardSx = {
  borderRadius: 3,
  border: `1px solid ${INK.gridline}`,
  boxShadow: 'none',
};

// Kiosk installation with a phone only - full reference:
// documentations/kiosk-installation.md (backend routes/DeviceClaim.ts,
// routes/kioskClaim.ts, firmware 0.5.0).
const KIOSK_STEPS = [
  'Branchez la borne : elle affiche « Configuration » avec un QR code et un code PIN.',
  'Dans l’app ESP BLE Provisioning : Provision New Device, scannez ce QR (ou choisissez le nom PROV_… et tapez le PIN), choisissez le Wi-Fi du restaurant et tapez son mot de passe.',
  'La borne se connecte et affiche « Associer cette borne » avec un autre QR et un code (ex. K7MP-2QXR).',
  'Scannez ce QR avec l’appareil photo du téléphone (pas avec l’app Espressif) : la page QRProofs s’ouvre.',
  'Touchez l’établissement (les vôtres sans borne sont en tête), puis le bouton vert « Associer » sur sa ligne. Autorisez la localisation : sur place, l’établissement est validé du même coup. S’il avait déjà une borne, l’ancienne s’arrêtera.',
  'La borne affiche « Borne prête » puis l’écran de notation : touchez le bouton, un QR doit s’afficher 10 secondes.',
];

const KIOSK_TROUBLESHOOTING = [
  ['« Mot de passe Wi-Fi incorrect »', 'Corrigez-le dans l’app et réessayez.'],
  ['« Reseau introuvable (2,4 GHz seulement) »', 'Choisissez le réseau 2,4 GHz de la box, rapprochez la borne.'],
  ['L’app ne trouve pas la borne', 'Activez Bluetooth et localisation sur le téléphone ; la borne doit afficher « Configuration » ou « Changer le Wi-Fi ».'],
  ['« Serveur injoignable, nouvel essai... »', 'Le Wi-Fi n’a pas accès à Internet (portail d’hôtel…) : utilisez un autre réseau.'],
  ['La page dit « code expiré »', 'Le code change toutes les 15 min : rescannez l’écran.'],
  ['L’établissement n’est pas dans la liste', 'Un prospect réservé n’est pas encore un établissement : créez-le depuis sa fiche dans l’appli terrain (« Créer l’établissement »), puis rescannez. Sinon il ne vous est pas attribué : demandez à un admin, ou faites associer par le gérant.'],
  ['« Cette borne est déjà associée »', 'Elle l’est déjà ; pour la changer d’établissement, utilisez « Changer d’établissement » dans son menu (appui long).'],
  ['La borne installée affiche « Associer cette borne »', 'Sa clé a été refusée (détachée, associée ailleurs ou nouvelle clé générée) : scannez le code pour la réassocier.'],
];

// Main pipeline, in order. `wired: true` means something in this
// codebase actually sets this status today (an automatic system event,
// or a real page); `wired: false` means the backend supports the
// transition (the generic PUT /leads/:id) but nothing currently calls it.
const PIPELINE = [
  { status: 'PROSPECT', wired: true },
  { status: 'CONTACTED', wired: true },
  { status: 'DEMO', wired: true },
  { status: 'TRIAL', wired: true },
  { status: 'ACTIVATED', wired: true },
  { status: 'PAID', wired: true },
  { status: 'CANCELLATION_SCHEDULED', wired: true },
  { status: 'CHURNED', wired: true },
];

const STAGE_DETAILS = [
  {
    status: 'PROSPECT',
    trigger: 'Import SIRENE des établissements, ou « Ajouter » dans l’appli terrain par un ambassadeur devant l’établissement.',
    where: 'Appli terrain /ambassador/new (un admin revoit les ajouts : Ambassadeurs › Prospects).',
    wired: true,
  },
  {
    status: 'CONTACTED',
    trigger: 'Résultat de visite « À rappeler / repasser » : le prospect passe en relance pour cet ambassadeur.',
    where: 'Appli terrain, fiche du prospect, après « Je suis sur place ».',
    wired: true,
  },
  {
    status: 'DEMO',
    trigger: 'Résultat de visite « Intéressé » ou « Installation acceptée », ou formulaire public « Book a demo ».',
    where: 'Appli terrain (fiche du prospect) ; page d’accueil /, avec un ?ref=CODE optionnel pour attribuer le lead.',
    wired: true,
  },
  {
    status: 'TRIAL',
    trigger: 'L’ambassadeur crée l’établissement sur place (« Créer l’établissement »), ou le gérant s’inscrit lui-même par un lien de parrainage.',
    where: 'Appli terrain, fiche du prospect ; inscription du gérant avec ?ref=CODE. Le lead est alors lié à l’établissement.',
    wired: true,
  },
  {
    status: 'ACTIVATED',
    trigger: 'Webhook Paddle : l’abonnement passe à trialing alors que le lead est encore avant l’activation (PROSPECT à TRIAL).',
    where: 'Événement système, aucune page — backend/routes/webhooks/paddle.ts.',
    wired: true,
  },
  {
    status: 'PAID',
    trigger: 'Webhook Paddle : l’abonnement passe à active (couvre aussi les renouvellements).',
    where: 'Événement système, aucune page.',
    wired: true,
  },
  {
    status: 'CANCELLATION_SCHEDULED',
    trigger: 'Webhook Paddle : la mise à jour de l’abonnement porte une annulation programmée.',
    where: 'Événement système, aucune page.',
    wired: true,
  },
  {
    status: 'CHURNED',
    trigger: 'Webhook Paddle : subscription.canceled — capture le dernier MRR connu juste avant qu’il soit remis à zéro. Aussi quand le gérant supprime un établissement payant.',
    where: 'Événement système, aucune page. Peut survenir directement depuis PAID, sans passer par CANCELLATION_SCHEDULED.',
    wired: true,
  },
  {
    status: 'LOST',
    trigger: 'Résultat de visite « Refus » : le prospect n’est plus proposé pendant le délai de refus (réglage), puis redevient visitable.',
    where: 'Appli terrain, fiche du prospect.',
    wired: true,
  },
];

// Pages where people act. `admin: true` = ROLE_ADMIN only.
const PAGES = [
  { name: 'Appli terrain', path: '/ambassador', admin: false,
    what: 'L’outil de l’ambassadeur sur son téléphone : carte des prospects, réservation, « Je suis sur place » et résultat de visite, ajout d’un établissement manquant, création de l’établissement sur place et transfert au gérant.' },
  { name: 'Associer une borne', path: '/kiosk/claim/:code', admin: false,
    what: 'Ouverte en scannant le QR d’une borne : choisir l’établissement. Sur place avec la localisation, l’ambassadeur valide l’établissement du même coup.' },
  { name: 'Dashboard commercial', path: '/admin/commercial', admin: false,
    what: 'Vue d’ensemble en lecture seule — KPI, funnel, courbe MRR, performance par zone, classement des ambassadeurs.' },
  { name: 'Profil ambassadeur', path: '/admin/commercial/agent/:id', admin: false,
    what: 'Son lien de démo personnel, ses KPI (leads, essais, payants, MRR, commission) et l’e-mail de bienvenue (renvoyer).' },
  { name: 'Fiche établissement', path: '/admin/commercial/facility/:id', admin: false,
    what: 'Offre, MRR, journal d’activité ; carte « Kiosk de notation » (Détacher la borne) et carte « Propriétaire » (transférer au gérant, invitation en attente).' },
  { name: 'Ambassadeurs', path: '/admin/commercial/agents', admin: true,
    what: 'Créer, modifier, désactiver un ambassadeur ; son accès et l’e-mail de bienvenue partent à la création.' },
  { name: 'Attribution des leads', path: '/admin/commercial/leads', admin: true,
    what: 'Tous les leads, filtrables par statut ou non assignés, à rattacher à un ambassadeur (rattachement seulement, pas de changement de statut).' },
  { name: 'Ambassadeurs › Territoires, Prospects, Visites, Réglages', path: '/admin/commercial/ambassador/…', admin: true,
    what: 'Dessiner les territoires ; revoir les prospects ajoutés et les doublons, libérer une réservation ; valider les visites dont le GPS ne correspondait pas ; régler les nombres des règles (durée de réservation, relance, distances GPS…).' },
  { name: 'Manager board', path: '/ManagerDashboard', admin: true,
    what: 'Valider les établissements, « Transfer », « Detach kiosk », et la section « Facility transfers » : où en est chaque gérant invité (compte, e-mail vérifié, nom, accepté), avec « Resend e-mail ».' },
];

export default function CommercialGuide() {
  const [kcInstance, setKcInstance] = useState(null);
  const [token, setToken] = useState('');
  const [forbidden, setForbidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const loginRequestedRef = useRef(false);

  useEffect(() => {
    (async () => {
      const kc = await initKeycloak();
      window.history.replaceState(null, '', window.location.origin + window.location.pathname);

      if (!kc.authenticated) {
        if (loginRequestedRef.current) return;
        loginRequestedRef.current = true;
        kc.login({ redirectUri: window.location.href });
        return;
      }
      if (!kc.hasRealmRole('ROLE_ADMIN') && !kc.hasRealmRole('ROLE_SALES')) {
        setForbidden(true);
        setLoading(false);
        return;
      }
      setKcInstance(kc);
      setToken(kc.token ?? '');
      setLoading(false);
    })().catch(console.error);
  }, []);

  if (loading) {
    return <Typography sx={{ mt: 4, textAlign: 'center' }}>Chargement…</Typography>;
  }
  if (forbidden) {
    return <Typography sx={{ mt: 4, textAlign: 'center' }}>Accès refusé.</Typography>;
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Link component={RouterLink} to="/admin/commercial" sx={{ display: 'block', mb: 1 }}>
            ← Retour au dashboard
          </Link>
          <Typography variant="h4">Documentation commerciale</Typography>
        </Box>
        <Button variant="outlined" onClick={() => kcInstance?.logout({ redirectUri: `${window.location.origin}/` })}>
          Logout
        </Button>
      </Box>
      <Typography sx={{ color: INK.muted, mb: 4, maxWidth: 640 }}>
        Comment un lead avance réellement dans le pipeline aujourd’hui : ce qui déclenche chaque étape et la
        page où ça se passe ; puis l’installation des bornes et le transfert de l’établissement au gérant.
      </Typography>

      {/* ---------- Visual pipeline ---------- */}
      <Paper elevation={0} sx={{ ...cardSx, p: 3, mb: 3 }}>
        <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <CheckCircleIcon sx={{ fontSize: 16, color: INK.success }} />
            <Typography variant="caption" sx={{ color: INK.muted }}>En direct aujourd’hui</Typography>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap rowGap={1.5}>
          {PIPELINE.map((stage, i) => (
            <React.Fragment key={stage.status}>
              <Chip
                label={statusLabel(stage.status)}
                icon={stage.wired
                  ? <CheckCircleIcon sx={{ fontSize: '16px !important', color: '#fff !important' }} />
                  : <RadioButtonUncheckedIcon sx={{ fontSize: '16px !important', color: '#fff !important' }} />}
                sx={{
                  bgcolor: statusColor(stage.status),
                  color: '#fff',
                  fontWeight: 700,
                  opacity: stage.wired ? 1 : 0.55,
                }}
              />
              {i < PIPELINE.length - 1 && <ArrowForwardIcon sx={{ fontSize: 18, color: INK.gridline }} />}
            </React.Fragment>
          ))}
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
          <SubdirectoryArrowRightIcon sx={{ fontSize: 18, color: INK.muted }} />
          <Chip
            label={statusLabel('LOST')}
            icon={<CheckCircleIcon sx={{ fontSize: '16px !important', color: '#fff !important' }} />}
            sx={{ bgcolor: statusColor('LOST'), color: '#fff', fontWeight: 700 }}
          />
          <Typography variant="caption" sx={{ color: INK.muted }}>
            après un refus en visite (appli terrain) ; redevient visitable après le délai de refus
          </Typography>
        </Stack>
      </Paper>

      {/* ---------- Where people act ---------- */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>Où agir</Typography>
      <Typography sx={{ color: INK.muted, mb: 2, fontSize: 14 }}>
        Les ambassadeurs travaillent dans l’appli terrain et sur la page d’association des bornes ; les pages du
        dashboard commercial leur montrent leurs propres chiffres et établissements. Les pages marquées « Admin »
        sont réservées aux administrateurs.
      </Typography>
      <Stack spacing={1.5} sx={{ mb: 4 }}>
        {PAGES.map((pg) => (
          <Paper key={pg.path} elevation={0} sx={{ ...cardSx, p: 2 }}>
            <Typography sx={{ fontWeight: 700 }}>
              {pg.name}
              {pg.admin && <Chip size="small" label="Admin" sx={{ ml: 1, bgcolor: INK.gridline, color: INK.secondary }} />}
            </Typography>
            <Typography sx={{ color: INK.muted, fontSize: 14, mt: 0.5 }}>{pg.what}</Typography>
            <Typography sx={{ fontFamily: 'monospace', fontSize: 13, color: INK.secondary, mt: 0.5 }}>{pg.path}</Typography>
          </Paper>
        ))}
      </Stack>

      {/* ---------- Stage table ---------- */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>Étape par étape</Typography>
      <Paper elevation={0} sx={{ ...cardSx, mb: 4, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Statut</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Déclencheur & emplacement</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">État</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {STAGE_DETAILS.map((s) => (
              <TableRow key={s.status}>
                <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                  <Chip size="small" label={statusLabel(s.status)} sx={{ bgcolor: statusColor(s.status), color: '#fff', fontWeight: 700 }} />
                </TableCell>
                <TableCell sx={{ fontSize: 13.5 }}>
                  <Typography sx={{ fontSize: 13.5 }}>{s.trigger}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: INK.muted, mt: 0.5 }}>{s.where}</Typography>
                </TableCell>
                <TableCell align="right" sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                  {s.wired ? (
                    <Chip size="small" label={s.note ? 'Ignoré' : 'En direct'} sx={{ bgcolor: INK.success, color: '#fff' }} />
                  ) : (
                    <Chip size="small" variant="outlined" label={s.note ? 'Non utilisé' : 'Non câblé'} sx={{ borderColor: INK.gridline, color: INK.muted }} />
                  )}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>Lien facility</Typography>
              </TableCell>
              <TableCell sx={{ fontSize: 13.5 }}>
                <Typography sx={{ fontSize: 13.5 }}>
                  Rattacher un lead à son facility_id, nécessaire pour les rollups MRR/zone.
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: INK.muted, mt: 0.5 }}>
                  Automatique quand l’ambassadeur crée l’établissement sur place ou quand le gérant s’inscrit par un
                  lien de parrainage. Pour les autres (ex. un « Book a demo » suivi d’une inscription sans code),
                  aucune interface : seulement PUT /leads/:id.
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                <Chip size="small" label="En partie" sx={{ bgcolor: INK.success, color: '#fff' }} />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>

      {/* ---------- New ambassador ---------- */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>Nouvel ambassadeur</Typography>
      <Paper elevation={0} sx={{ ...cardSx, p: 2.5, mb: 4 }}>
        <Typography sx={{ fontSize: 14, lineHeight: 1.5 }}>
          Un admin le crée dans « Ambassadeurs ». Il reçoit automatiquement son accès (compte QRProofs créé si
          besoin, lien pour choisir son mot de passe) et un e-mail de bienvenue : comment prospecter, installer
          une borne, son code et son lien de parrainage, et où trouver cette documentation. L’e-mail peut être
          renvoyé depuis sa fiche. Désactiver un ambassadeur lui retire l’accès.
        </Typography>
      </Paper>

      {/* ---------- Kiosk installation ---------- */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>Installer une borne (téléphone seulement)</Typography>
      <Typography sx={{ color: INK.muted, mb: 2, fontSize: 14 }}>
        La borne arrive avec le logiciel QRProofs déjà installé. Il vous faut : l’établissement créé et attribué à
        vous (ou créé par vous sur place, voir ci-dessous), le nom et le mot de passe du Wi-Fi 2,4 GHz du restaurant, et l’app gratuite ESP BLE Provisioning
        (Espressif) sur votre téléphone.
      </Typography>
      <Paper elevation={0} sx={{ ...cardSx, p: 2.5, mb: 2 }}>
        <Box component="ol" sx={{ m: 0, pl: 2.5, '& li': { fontSize: 14, mb: 1, lineHeight: 1.5 } }}>
          {KIOSK_STEPS.map((step) => <li key={step}>{step}</li>)}
        </Box>
      </Paper>
      <Paper elevation={0} sx={{ ...cardSx, p: 2.5, mb: 2 }}>
        <Typography sx={{ fontWeight: 700, mb: 1 }}>Le gérant n’a pas encore de compte ? Créer l’établissement puis le transférer</Typography>
        <Typography sx={{ fontSize: 14, lineHeight: 1.5 }}>
          Dans l’appli terrain, fiche du prospect (réservé pour vous ou en relance) : « Créer l’établissement ». Il est
          créé à votre nom avec les informations de la fiche, sans limite d’essai : installez la borne. Puis
          « Transférer au gérant » avec son e-mail (ou donnez-le dès la création) : il reçoit un lien valable 14 jours,
          se connecte ou crée son compte avec cette adresse et accepte. L’établissement est alors à lui, avec 30 jours
          d’essai gratuit ; la borne continue de fonctionner et vous restez l’ambassadeur attribué. Invitation en attente :
          Renvoyer ou Annuler sur la même fiche (ou sur la page de l’établissement ; « Transfer » sur le Manager board
          pour les admins).
        </Typography>
        <Typography sx={{ fontSize: 14, lineHeight: 1.5, mt: 1 }}>
          Validation : en associant la borne sur place avec la localisation du téléphone autorisée (à moins de 100 m de
          l’établissement), vous validez l’établissement aussitôt ; sinon un admin le valide. Même avant la validation,
          la borne, le QR et les statistiques fonctionnent pour un établissement créé par vous : la démo de scan est
          possible dès l’installation. La validation sert aux pages publiques (carte, listes, badges).
        </Typography>
      </Paper>
      <Paper elevation={0} sx={{ ...cardSx, p: 2.5, mb: 2 }}>
        <Typography sx={{ fontWeight: 700, mb: 1 }}>Changer le Wi-Fi d’une borne installée</Typography>
        <Typography sx={{ fontSize: 14, lineHeight: 1.5 }}>
          Maintenez le doigt 5 secondes dans le coin en haut à droite de l’écran : la borne affiche « Changer le
          Wi-Fi ». Donnez le nouveau Wi-Fi avec l’app ESP BLE Provisioning. « Annuler » ou 10 minutes sans rien :
          elle reprend l’ancien Wi-Fi. Sans Wi-Fi pendant 15 minutes, l’écran s’ouvre tout seul.
        </Typography>
      </Paper>
      <Paper elevation={0} sx={{ ...cardSx, p: 2.5, mb: 2 }}>
        <Typography sx={{ fontWeight: 700, mb: 1 }}>Changer une borne d’établissement</Typography>
        <Typography sx={{ fontSize: 14, lineHeight: 1.5 }}>
          Sur place : appui long 5 secondes dans le coin en haut à droite → « Changer d’établissement ». La borne
          affiche un QR ; scannez-le et choisissez le nouvel établissement. Tant que personne ne l’a associée
          ailleurs, elle reste dans son établissement (« Annuler » ou 15 minutes : rien ne change). À distance :
          bouton « Détacher la borne » sur la carte « Kiosk de notation » de la fiche établissement (ou « Detach
          kiosk » sur le Manager board pour les admins) ; la borne affiche son code d’association dans les 5 minutes.
        </Typography>
      </Paper>
      <Paper elevation={0} sx={{ ...cardSx, mb: 4, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Ce que vous voyez</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Que faire</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {KIOSK_TROUBLESHOOTING.map(([seen, todo]) => (
              <TableRow key={seen}>
                <TableCell sx={{ fontSize: 13.5, verticalAlign: 'top' }}>{seen}</TableCell>
                <TableCell sx={{ fontSize: 13.5 }}>{todo}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* ---------- Landing pages and Google Ads ---------- */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>Pages d’entrée et Google Ads</Typography>
      <Typography sx={{ color: INK.muted, mb: 2, fontSize: 14 }}>
        Une page d’entrée par fonctionnalité, chacune avec son propre design, pour les campagnes Google Ads, les
        flyers et les e-mails des ambassadeurs. Le but : savoir quelle page et quels mots-clés apportent le plus de
        démos et d’inscriptions. Toutes passent par l’accueil avec un paramètre : qrproofs.com/?page=borne ; sans
        paramètre, l’accueil habituel s’affiche. Le paramètre ?ref= d’un ambassadeur s’ajoute si besoin
        (?page=borne&ref=CODE). L’origine (page, campagne, mot-clé) s’affiche sur la fiche du prospect et dans
        /admin/landings.
      </Typography>
      <Paper elevation={0} sx={{ ...cardSx, p: 2.5, mb: 2 }}>
        <Typography sx={{ fontWeight: 700, mb: 1 }}>Où en est-on</Typography>
        <Stack spacing={0.75}>
          {LANDING_STATUS.map(([label, done]) => (
            <Stack key={label} direction="row" spacing={1} alignItems="center">
              {done
                ? <CheckCircleIcon sx={{ fontSize: 18, color: INK.success }} />
                : <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: INK.muted }} />}
              <Typography sx={{ fontSize: 14 }}>{label}</Typography>
              <Chip size="small" variant={done ? 'filled' : 'outlined'} label={done ? 'Prêt' : 'En préparation'}
                sx={done ? { bgcolor: INK.success, color: '#fff' } : { borderColor: INK.gridline, color: INK.muted }} />
            </Stack>
          ))}
        </Stack>
        <Typography sx={{ fontSize: 13, color: INK.muted, mt: 1.5 }}>
          Les campagnes peuvent démarrer : visites, démos et inscriptions sont comptées par page et par mot-clé dans
          /admin/landings. Sans bandeau cookies ni balise Google, les conversions ne remontent pas encore dans Google
          Ads : lisez-les dans /admin/landings.
        </Typography>
      </Paper>
      <Paper elevation={0} sx={{ ...cardSx, mb: 2, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Page</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Pour qui</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Mots-clés de départ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {LANDING_PAGES.map(([path, name, who, keywords]) => (
              <TableRow key={path}>
                <TableCell sx={{ fontSize: 13.5, verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                  <b>{name}</b><br /><code>qrproofs.com{path}</code>
                </TableCell>
                <TableCell sx={{ fontSize: 13.5, verticalAlign: 'top' }}>{who}</TableCell>
                <TableCell sx={{ fontSize: 13.5 }}>{keywords}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      <Paper elevation={0} sx={{ ...cardSx, p: 2.5, mb: 2 }}>
        <Typography sx={{ fontWeight: 700, mb: 1 }}>Lancer et lire une campagne par page</Typography>
        <Box component="ol" sx={{ m: 0, pl: 2.5, '& li': { fontSize: 14, mb: 1, lineHeight: 1.5 } }}>
          {GOOGLE_ADS_STEPS.map((step) => <li key={step}>{step}</li>)}
        </Box>
      </Paper>
      <Paper elevation={0} sx={{ ...cardSx, p: 2.5, mb: 4 }}>
        <Typography sx={{ fontWeight: 700, mb: 1 }}>Deux chiffres qui ne coïncident pas : c’est normal</Typography>
        <Typography sx={{ fontSize: 14, lineHeight: 1.5 }}>
          Quand le bandeau cookies et la balise Google seront en place, Google Ads ne comptera que les visiteurs qui
          auront accepté les cookies. Le tableau de bord
          QRProofs /admin/landings compte tout le monde, sans cookie et sans donnée personnelle. Pour comparer les
          pages entre elles, prenez une seule source : le tableau de bord QRProofs pour les volumes, Google Ads pour
          le coût par conversion.
        </Typography>
      </Paper>

      {/* ---------- Gaps ---------- */}
      <Paper
        elevation={0}
        sx={{
          ...cardSx,
          p: 3,
          mb: 3,
          bgcolor: '#fff8ea',
          borderColor: '#f0c869',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: '#8a5a00' }}>Ce qui manque</Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { fontSize: 14, mb: 1, lineHeight: 1.5 } }}>
          <li>
            Pas de changement de statut à la main dans le dashboard commercial : un lead avance par les visites de
            l’appli terrain, la création ou l’inscription de l’établissement, puis le webhook Paddle. Un admin ne peut
            pas, par exemple, marquer perdu un lead qui n’a pas été visité.
          </li>
          <li>
            Un lead « Book a demo » dont le gérant s’inscrit ensuite sans code de parrainage n’est pas relié à son
            établissement : il faut modifier commercial_lead.facility_id directement pour qu’il compte dans les
            rollups MRR et zone.
          </li>
          <li>
            Pages d’entrée : pas encore de bandeau cookies ni de balise Google Ads. Les conversions (démos,
            inscriptions) se lisent dans /admin/landings, pas encore dans Google Ads, qui ne peut donc pas optimiser
            les enchères sur les conversions.
          </li>
          <li>
            Les leads hors appli terrain (« Book a demo », inscriptions) ne peuvent être rattachés à un ambassadeur que
            par un admin (Attribution des leads) ; l’ambassadeur ne peut réclamer lui-même que les prospects de la carte.
          </li>
        </Box>
      </Paper>

      <Divider sx={{ mb: 2 }} />
      <Typography variant="caption" sx={{ color: INK.muted }}>
        Établi à partir de backend/utils/ambassador/ (visites, prospects, création sur place),
        backend/routes/facilities_v2.ts, backend/utils/commercialLeadSync.ts, backend/routes/webhooks/paddle.ts,
        backend/routes/publicBookDemo.ts, backend/scripts/importSirene.ts, backend/utils/facilityTransfer.ts,
        backend/utils/kioskAutoApprove.ts et des pages sous frontend/src/components/. Mise à jour : 22 octobre 2026.
      </Typography>
    </Container>
  );
}
